import React, { useState, useEffect } from "react";
import {Button, createTheme, Grid, IconButton, InputBase, ThemeProvider, Typography} from '@material-ui/core';
import {useHistory} from 'react-router-dom';
import useStyles from '../../assets/styles';
import { clickBack, devicePulseStatus, ticketColumns } from '../../../utils';
import { RedirectIcon } from '../../../../components/Icons';
import { createJiraProject, createServiceNowProject, getAllJiraStatuses, getAllServiceNowStatuses, getJiraProject, getServiceNowColumns, getServiceNowProject, validateServiceNowConfiguration } from "../components/apiHelper";
import { Loader } from "../components/Loader";
import { ToasterComponent } from "../components/Toaster";
import { primaryGray } from "src/components/colors";
import { JiraDetailsComponent } from "./JiraDetialsComponent";
import { StatusMappingComponent } from "./StatusMappingComponent";
import { ServiceNowColumn, JiraProjectData, ServiceNowProjectData, TicketColumnsEnum,  } from "src/app/models";
import { ITSMTitleComponent } from "./ITSMTitleComponent";
import { ServiceNowDetailsComponent } from "./ServiceNowDetailsComponent";
import { set } from "date-fns";

const STEPS = [
    {
      text: "ServiceNow Details",
      activate: true,
    },
    {
      text: "Status Mapping",
      activate: false,
    }
];

export const ServiceNowConfigurationComponent = () => {
    const theme = createTheme({
        typography: {
            fontFamily: "'Poppins', sans-serif",
        },
    });
    const classes = useStyles();
    const history = useHistory();
    const [steps, setSteps] = useState(STEPS);
    const [step, setStep] = useState(0);
    const [serviceNowBoard, setServiceNowBoard] = useState("");
    const [serviceNowURL, setServiceNowURL] = useState("");
    const [username, setusername] = useState("");
    const [password, setPassword] = useState("");
    const [tableName, setTableName] = useState("");
    const [isprojectExist, setIsProjectExist] = useState(false);
    const [statusMapping, setStatusMapping] = useState({});
    const [jiraStatuses, setJiraStatuses] = useState([]);
    const [isInputDisabled, setIsInputDisabled] = useState(true);
    const [isConfirmClick, setIsConfirmClick] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [projectDetail, setProjectDetail] = useState({
        serviceNowBoard: "",
        serviceNowURL: "",
        password: "",
        username: "",
        tableName: "",
        statusColumnName: "",
        columnsMap: {
            [TicketColumnsEnum.TICKET_ID]: "",
            [TicketColumnsEnum.TICKET_SUMMARY]: "",
            [TicketColumnsEnum.TICKET_DESCRIPTION]: "",
            [TicketColumnsEnum.TICKET_STATUS]: "",
            [TicketColumnsEnum.TICKET_PRIORITY]: "",
            [TicketColumnsEnum.ASSIGNED_TO]: "",
            [TicketColumnsEnum.REPORTER]: ""
        }
    });
    const [errors, setErrors] = useState({
        nameError: null,
        urlError: null,
        usernameError: null,
        passwordError: null,
        tableNameError:null
    })
    const [mappingErrors, setMappingErrors] = useState<any>({});
    const [columns, setColumns] = useState<ServiceNowColumn[]>([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [columnErrors, setColumnErrors] = useState<Record<string, string>>({});       

    //Toaster
    const [isToasterOpen, setIsToasterOpen] = useState(false)
    const [toasterMessage, setToasterMessage] = useState("")
    const [reqSuccess, setReqSuccess] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        updateStepActivation(step);
    }, [step]);
    
    function updateStepActivation(currentStep) {
        const updatedSteps = steps.map((step, index) => ({
            ...step,
            activate: index === currentStep,
        }));
        setSteps(updatedSteps);
    }

    const handleJiraStatusChange = (index: number, selectedStatusLabel: string) => {
        setStatusMapping((prev) => {
            const updatedMapping = {
                ...prev,
                [index]: selectedStatusLabel,
            };

            setMappingErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (selectedStatusLabel && selectedStatusLabel.trim() !== "") {
                    newErrors[index] = null;
                }
                return newErrors;
            });

            return updatedMapping;
        });
    };

    const handleInputChange = async (event) => {
        const { name, value } = event.target;
        if(name === "boardName"){
            setServiceNowBoard(value);
            setErrors({...errors, nameError: null});
        }
        else if(name === "url"){
            setServiceNowURL(value);
            setErrors({...errors, urlError: null});
        }
        else if(name === "password"){
            setPassword(value);
            setErrors({...errors, passwordError: null});
        }
        else if(name === "username"){
            setusername(value);
            setErrors({...errors, usernameError: null});
        }
    };

    const resetFormFields = () => {
        setServiceNowBoard("");
        setServiceNowURL("");
        setPassword("");
        setusername("");
        setTableName("");
        setErrors({
            nameError: null,
            urlError: null,
            passwordError: null,
            usernameError: null,
            tableNameError:null
        });
    }

    const handleBack = () => {
        setStep(0);
        setSteps(STEPS);
        resetFormFields();
        history.push("/Configuration");
    }

    const handleRedirect = () => {
        history.push({
            pathname: "/deviceFailure",
            state: { isRedirect: true }
        });
        const event = new CustomEvent('deviceFailureClicked');
        window.dispatchEvent(event);
    }

    const handleTableSelect = (selectedTableName) => {
        setTableName(selectedTableName);
    };

    const fetchColumnNames = async (tableName) => {
        if (!tableName) return;
        
        try {
            setLoading(true);
            const response = await getServiceNowColumns();
            if (response.status === 200) {
                setColumns(response.data.result || []);      
            } else {
                console.log("Error fetching data");
            }
        } catch(error) {
            setIsToasterOpen(true);
            setReqSuccess(false);
            setToasterMessage(error.message);
        } finally {
            setLoading(false);
        }
    }

    const fetchAllServiceNowStatuses = async () => {
        try {
            setLoading(true);
            const response = await getAllServiceNowStatuses();
            if (response.status === 200) {
                const data = response.data.result;

                const statusOptions = data.map((status) => ({
                    label: status.label,  // Display text in dropdown
                    value: status.value  // Actual value to be sent in payload
                }));

                const uniqueStatusOptions = statusOptions.reduce((acc, current) => {
                    const exists = acc.some(item => item.value === current.value);
                    return exists ? acc : [...acc, current];
                }, []);

                setJiraStatuses(uniqueStatusOptions);
            }
        } catch(error) {
            setIsToasterOpen(true);
            setReqSuccess(false);
            setToasterMessage(error.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const getProjectDetail = async () => {
            try {
                setLoading(true);
                const response = await getServiceNowProject();
                if (response.status === 200) {
                    const data = response.data;
                    if(data.serviceNowBoard !== null && data.serviceNowBoard !== "" && data.serviceNowBoard !== undefined) {    
                        setIsProjectExist(true);
                        setProjectDetail(data);
                    }
                } else{
                    console.log("Error fetching data");
                }
            } catch(error) {
            } finally {
                setLoading(false);
            }
        }
        getProjectDetail();
    }, []);

    const validationForm = async () => {
        let isError = false;
        let errors: any = {};
        if(tableName.trim() === ""){
            errors.keyError = "Selecting a Table Required";
            isError = true;
        }
        setErrors(errors);
        return isError;
    }

    const validateFields = () => {
        let isError = false;
        let errors: any = {};
        if(serviceNowBoard.trim() === ""){
            errors.nameError = "ServiceNow Board Name Required";
            isError = true;
        }
        if(serviceNowURL.trim() === ""){
            errors.urlError =  "Servicenow Url Required";
            isError = true;
        }
        if(username.trim() === ""){
            errors.usernameError = "Admin Username Required";
            isError = true;
        } if(password.trim() === ""){
            errors.passwordError = "Admin Password Required";
            isError = true;
        }
        setErrors(errors);
        return isError;
    }

    const validateStatusMapping = () => {
        let hasError = false;
        const errors: Record<number, string> = {};
    
        devicePulseStatus.forEach((statusObj, index) => {
            const selectedStatus = statusMapping[index];
            if (!selectedStatus || selectedStatus.trim() === "") {
                errors[index] = `Mapping this status is required`;
                hasError = true;
            }
        });
    
        setMappingErrors(errors);
        return hasError;
    }; 

    const handleColumnMappingChange = (field: TicketColumnsEnum, value: string) => {
        const elementMatch = value.match(/\(([^)]+)\)/);
        const elementValue = elementMatch ? elementMatch[1].trim() : value.trim();
        
        setColumnMapping(prev => ({
            ...prev,
            [field]: elementValue
        }));
        
        setColumnErrors(prev => ({
            ...prev,
            [field]: ""
        }));
    };

    const validateColumnMapping = (): boolean => {
        let hasError = false;
        const errors: Record<TicketColumnsEnum, string> = {} as Record<TicketColumnsEnum, string>;

        ticketColumns.forEach((columnObj) => {
            const selectedColumn = columnMapping[columnObj.column];
            if (!selectedColumn || selectedColumn.trim() === "") {
                errors[columnObj.column] = `Mapping for ${columnObj.column.replace(/_/g, ' ')} is required`;
                hasError = true;
            } else {
                errors[columnObj.column] = "";
            }
        });

        setColumnErrors(errors);
        return hasError;
    };

    const getAvailableOptions = (currentField: string) => {
        const selectedValues = Object.entries(columnMapping)
            .filter(([key]) => key !== currentField)
            .map(([_, value]) => value);

        return (columns || [])
            .filter((col: ServiceNowColumn) => 
                col.element && !selectedValues.includes(col.element)
            )
            .map((col: ServiceNowColumn) => 
                `${col.column_label || col.element} (${col.element})`
            );
    };

    const handleValidate = async () => {
        if(step === 0) {
            const validationError = await validateFields();
            if (validationError) {
                return;
            }
        }
        let data = {
            serviceNowBoard: serviceNowBoard.trim(),
            serviceNowURL: serviceNowURL.trim(),
            username: username.trim(),
            password: password.trim(),
        }
        try{
            setLoading(true);
            const response = await validateServiceNowConfiguration(data);
            console.log("response", response);
            if(response.status === 200){
                setIsValidated(true);
                setValidationError("");
                setIsToasterOpen(true);
                setReqSuccess(true);
                setToasterMessage("Configurations Validated Successfully");
            }
            setIsInputDisabled(false);
            setIsConfirmClick(true);
        } catch (error) {
            setIsValidated(false);
            setIsInputDisabled(true);
            if(error.status === 400){
                setIsToasterOpen(true);
                setReqSuccess(false);
                setToasterMessage(error.data);
            }
        } finally {
            setLoading(false)
            setTimeout(() => setIsToasterOpen(false), 2000);
        }
    }

    const handlePartialSubmit = async () => {
        let data: ServiceNowProjectData = {
            serviceNowBoard: serviceNowBoard.trim(),
            serviceNowURL: serviceNowURL.trim(),
            username: username.trim(),
            password: password.trim(),
            tableName: tableName.trim()
        };
        try {
            setLoading(true);
            const response = await createServiceNowProject(data);
            if(response.status === 200){
                fetchColumnNames(tableName);
                setIsToasterOpen(true);
                setReqSuccess(true);
                setToasterMessage("Initial Details Saved Successfully");
            } 
        } catch (error) {
            setIsToasterOpen(true);
            setReqSuccess(false);
            setToasterMessage(error.data.message);
        } finally {
            setLoading(false)
            setTimeout(() => setIsToasterOpen(false), 2000);
        }
    }


    const handleSubmit = async () => {
        console.log("step", step);
        if(step === 0){
            const validationError = await validationForm();
            const columnValidationError = validateColumnMapping();
            if (validationError  || columnValidationError) {
                return;
            }
        } else if (step === 1) {
            const mappingValidationError = validateStatusMapping();
            if (mappingValidationError) {
                return;
            }
        }

        const columnsMap = {};
            ticketColumns.forEach((column) => {
                const selectedColumn = columnMapping[column.column];
                if (selectedColumn && selectedColumn.trim() !== "") {
                    columnsMap[column.column] = selectedColumn.trim();
                }
            });

        let data: ServiceNowProjectData = {
            serviceNowBoard: serviceNowBoard.trim(),
            serviceNowURL: serviceNowURL.trim(),
            username: username.trim(),
            password: password.trim(),
            tableName: tableName.trim(),
            columnsMap,
            statusColumnName: columnMapping["TICKET_STATUS"] || "",
            status: true
        };
        if (step === 1) {
            const statusMap = {};
            devicePulseStatus.forEach((item, index) => {
                const selectedStatus = statusMapping[index];
                if (selectedStatus) {
                    const statusObj = jiraStatuses.find(s => s.label === selectedStatus);
                    if (statusObj) {
                        statusMap[item.status] = statusObj.value;
                    }
                }
            });
        
            data = {
                ...data,
                statusMap
            };
        }
        try {
            setLoading(true);
            const response = await createServiceNowProject(data);
            if(response.status === 200){
                setIsToasterOpen(true);
                setReqSuccess(true);
                setToasterMessage("Configurations Saved Successfully");
                if(step === 0){
                    setStep(1);
                    await fetchAllServiceNowStatuses();
                }
                if(step === 1){
                    setIsToasterOpen(true);
                    setReqSuccess(true);
                    setToasterMessage("Status Mapped Successfully");
                    resetFormFields();
                    setTimeout(() => history.push("/TestAutomation"), 2000);
                }
            } 
        } catch (error) {
            if(error.status === 422){
                setIsToasterOpen(true);
                setReqSuccess(false);
                setToasterMessage(error.data.message);
            }
        } finally {
            setLoading(false)
            setTimeout(() => setIsToasterOpen(false), 2000);
        }
    }

    function renderStepsComponent() {
        if(step === 0) {
            return <ServiceNowDetailsComponent 
                serviceNowBoard={serviceNowBoard}
                serviceNowURL={serviceNowURL}
                password={password}
                username={username}
                tableName={tableName}
                onTableSelect={handleTableSelect}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                handlePartialSubmit={handlePartialSubmit}
                handleRedirect={handleRedirect}
                decreaseStep={decreaseStep}
                increaseStep={increaseStep}
                handleValidate={handleValidate}
                nameError={errors.nameError}
                urlError={errors.urlError}
                usernameError={errors.usernameError}
                passwordError={errors.passwordError}
                tableNameError={errors.tableNameError}
                isInputDisabled={isInputDisabled}
                getAvailableOptions={getAvailableOptions}
                handleColumnMappingChange={handleColumnMappingChange}
                columnMapping={columnMapping}
                columnErrors={columnErrors}
                areDropdownsDisabled={!tableName || isInputDisabled}
                validationError={validationError}
                isprojectExist={isprojectExist}
                projectDetail={projectDetail}
            />
        } else if(step === 1) {
            return <StatusMappingComponent
                    devicePulseStatus={devicePulseStatus}
                    jiraStatuses={jiraStatuses}
                    increaseStep={increaseStep}
                    decreaseStep={decreaseStep}
                    handleJiraStatusChange={handleJiraStatusChange}
                    statusMapping={statusMapping}
                    setStatusMapping={setStatusMapping}
                    mappingErrors={mappingErrors}
                    isServiceNow={true}
            />
        }
    }

    async function increaseStep() {
        if (step === 0 && !isValidated) {
            setValidationError("Please Validate Your ServiceNow Credentials");
            return;
        }
        await handleSubmit();
    }    

    function decreaseStep() {
        if(step === 0){
            handleBack();
        }
        setStep(prevStep => 
            Math.max(prevStep - 1, 0)
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="testing">
                <ITSMTitleComponent />
                <Grid container className={classes.circleContainer}>
                    {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        <Grid item xs={2} md={2}>
                        <Grid container direction="column" alignItems="center">
                            <Grid item>
                            <Circle number={index + 1} active={step.activate} completed={steps.findIndex(s => s.activate) > index} />
                            </Grid>
                            <Grid item>
                            <Typography
                                style={{
                                fontSize: "12px",
                                paddingTop: "15px",
                                fontFamily: "Poppins, sans-serif",
                                color: step.activate ? '#005B96' : primaryGray,
                                }}
                            >
                                {step.text}
                            </Typography>
                            </Grid>
                        </Grid>
                        </Grid>
                        {index !== steps.length - 1 && (
                        <Grid item xs={2} md={2}>
                            <hr
                            style={{
                                marginTop: "32px",
                                opacity: steps[index + 1].activate ? "1" : "0.12",
                            }}
                            color={
                                steps[index + 1].activate
                                ? "#005B96"
                                : "rgba(112, 112, 112, .12)"
                            }
                            />
                        </Grid>
                        )}
                    </React.Fragment>
                    ))}
                </Grid>
                <Grid item style={{marginTop:'20px'}}>
                    {renderStepsComponent()}
                </Grid>                  
            </div>
            <Loader loading={loading} />
            <ToasterComponent 
                reqSuccess={reqSuccess} 
                message={toasterMessage} 
                toaster={isToasterOpen} 
            />
        </ThemeProvider>
    )
}

const Circle = (props) => {
  const paddedNumber = (props.number).toString().padStart(2, '0');
  return (
    <div>
      <div
        style={{
          borderRadius: "50%",
          width: "65px",
          height: "65px",
          backgroundColor: props.completed ? "#005B96" : "white",
          border: props.active ? "2px solid #005B96" : "0.25px solid #8F8F8F",
          textAlign: "center",
          paddingTop: "10px",
          boxShadow: "0px 4px 8px #0000001F",
        }}
      >
        <h5 style={{
          paddingTop: "10px",
          fontSize: "16px",
          color: props.active ? "#005B96" : (props.completed ? "white" : primaryGray)
        }}>
          {paddedNumber}
        </h5>
      </div>
    </div>
  );
};